import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  Add as AddIcon,
  CloudUpload as CloudUploadIcon,
  AccountCircle as AccountCircleIcon,
  ExitToApp as ExitToAppIcon,
  Edit as EditIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';

interface NavbarProps {
  onToggleMobileMenu?: () => void;
  onToggleDesktopSidebar?: () => void;
  sidebarExpanded?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({
  onToggleMobileMenu,
  onToggleDesktopSidebar,
  sidebarExpanded = true,
}) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
    navigate('/login');
  };

  const handleUploadClick = () => {
    navigate('/invoices/journey?flow=upload');
  };

  if (!user) return null;

  if (location.pathname.startsWith('/admin')) {
    return null;
  }

  // Derive a readable page title from current route
  const getPageTitle = (): string => {
    const path = location.pathname;
    if (path.includes('/dashboard')) return 'Dashboard';
    if (path.includes('/invoices/journey')) return 'Create Invoice';
    if (path.includes('/invoices')) return 'Invoices';
    if (path.includes('/business-details') || path.includes('/business')) return 'Business Profile';
    if (path.includes('/client-details')) return 'Clients';
    if (path.includes('/payment-accounts')) return 'Payment Accounts';
    if (path.includes('/item-details')) return 'Products & Items';
    if (path.includes('/shipping-details')) return 'Shipping Details';
    if (path.includes('/profile')) return 'User Profile';
    if (path.includes('/upgrade')) return 'OCR Premium';
    if (path.includes('/support')) return 'Support';
    return 'Billing Portal';
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: 'rgba(255, 255, 255, 0.76)',
        backdropFilter: 'blur(14px) saturate(1.6)',
        WebkitBackdropFilter: 'blur(14px) saturate(1.6)',
        color: '#0f172a',
        borderBottom: '1px solid rgba(226, 232, 240, 0.9)',
        boxShadow: '0 2px 16px -6px rgba(11, 43, 74, 0.1)',
        zIndex: (theme) => theme.zIndex.appBar,
      }}
    >
      <Toolbar sx={{ minHeight: 64, px: { xs: 2, sm: 3 } }}>
        {/* Mobile menu toggle */}
        <IconButton
          edge="start"
          aria-label="open navigation menu"
          onClick={onToggleMobileMenu}
          sx={{
            display: { xs: 'inline-flex', md: 'none' },
            mr: 1.5,
            color: '#0b2b4a',
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Desktop collapse toggle */}
        <Tooltip title={sidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'}>
          <IconButton
            onClick={onToggleDesktopSidebar}
            sx={{
              display: { xs: 'none', md: 'inline-flex' },
              mr: 1.5,
              color: '#64748b',
              '&:hover': { color: '#0b2b4a', bgcolor: '#f1f5f9' },
            }}
          >
            {sidebarExpanded ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
        </Tooltip>

        {/* Page Title & Mobile Brand */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1 }}>
            <Box
              className="animate-pulse-soft"
              sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                background: 'linear-gradient(135deg, #eab308, #f59e0b)',
                color: '#0b2b4a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px -2px rgba(234, 179, 8, 0.6)',
              }}
            >
              <ReceiptIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0b2b4a' }}>
              Invokta
            </Typography>
          </Box>

          <Typography
            variant="h6"
            className="text-gradient-brand"
            sx={{
              display: { xs: 'none', sm: 'block' },
              fontWeight: 700,
              fontSize: '1.125rem',
              letterSpacing: '-0.01em',
            }}
          >
            {getPageTitle()}
          </Typography>
        </Box>

        {/* Action buttons & User profile */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={handleUploadClick}
            startIcon={<CloudUploadIcon />}
            size="small"
            className="glow-navy-hover"
            sx={{
              display: { xs: 'none', sm: 'inline-flex' },
              color: '#0b2b4a',
              borderColor: '#cbd5e1',
              borderRadius: '999px',
              fontWeight: 700,
              px: 1.75,
              py: 0.75,
              '&:hover': { bgcolor: '#f8fafc', borderColor: '#0b2b4a' },
            }}
          >
            Upload
          </Button>

          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/invoices/journey')}
            size="small"
            className="shimmer-sweep glow-gold"
            sx={{
              fontWeight: 700,
              borderRadius: '999px',
              px: 2,
              py: 0.75,
              color: '#0b2b4a',
              bgcolor: '#eab308',
            }}
          >
            New Invoice
          </Button>

          {/* User Menu */}
          <IconButton
            size="medium"
            edge="end"
            aria-label="account menu"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            sx={{
              ml: 0.5,
              p: 0.5,
              border: '2px solid #e2e8f0',
              transition: 'border-color 0.2s',
              '&:hover': { borderColor: '#eab308' },
            }}
          >
            <Avatar
              src={user.pictureUrl || undefined}
              sx={{
                width: 32,
                height: 32,
                bgcolor: '#0b2b4a',
                color: '#eab308',
                fontWeight: 700,
                fontSize: '0.875rem',
              }}
            >
              {user.full_name ? user.full_name.charAt(0).toUpperCase() : 'U'}
            </Avatar>
          </IconButton>

          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            slotProps={{
              paper: {
                elevation: 3,
                sx: {
                  minWidth: 220,
                  borderRadius: 2,
                  mt: 1,
                  boxShadow: '0 10px 25px -5px rgba(15, 23, 42, 0.1), 0 8px 10px -6px rgba(15, 23, 42, 0.1)',
                  border: '1px solid #e2e8f0',
                },
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a' }}>
                {user.full_name || 'User'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#64748b', display: 'block', wordBreak: 'break-all' }}>
                {user.email}
              </Typography>
            </Box>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem onClick={() => { handleClose(); navigate('/profile'); }} sx={{ py: 1, gap: 1 }}>
              <AccountCircleIcon fontSize="small" sx={{ color: '#64748b' }} />
              <Typography variant="body2" fontWeight={500}>View Profile</Typography>
            </MenuItem>
            <MenuItem onClick={() => { handleClose(); navigate('/profile?edit=true'); }} sx={{ py: 1, gap: 1 }}>
              <EditIcon fontSize="small" sx={{ color: '#64748b' }} />
              <Typography variant="body2" fontWeight={500}>Edit Profile</Typography>
            </MenuItem>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem onClick={handleLogout} sx={{ py: 1, gap: 1, color: '#ef4444' }}>
              <ExitToAppIcon fontSize="small" />
              <Typography variant="body2" fontWeight={600}>Logout</Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;